import React, { useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import HdfcLogo from "../../assets/hdfc-logo.png";
import IciciLogo from "../../assets/icici-logo.jpg";
import AxisLogo from "../../assets/axis-logo.png";
import KotakLogo from "../../assets/kotak-logo.png";
import IndusLogo from "../../assets/indusind-logo.jpg";
import TataLogo from "../../assets/tata-logo.jpg";
import PiramalLogo from "../../assets/piramal-logo.png";
import BajajLogo from "../../assets/bajaj-logo.jpg";
import IDFCLogo from "../../assets/idfc-logo.png";
import YesBankLogo from "../../assets/yesbank-logo.png";

const partners = [
  {
    id: 1,
    name: "HDFC Bank",
    logo: HdfcLogo,
  },
  {
    id: 2,
    name: "ICICI Bank",
    logo: IciciLogo,
  },
  {
    id: 3,
    name: "Axis Bank",
    logo: AxisLogo,
  },
  {
    id: 4,
    name: "Kotak Mahindra Bank",
    logo: KotakLogo,
  },
  {
    id: 5,
    name: "IndusInd Bank",
    logo: IndusLogo,
  },
  {
    id: 6,
    name: "Tata Capital",
    logo: TataLogo,
  },
  {
    id: 7,
    name: "Piramal Finance",
    logo: PiramalLogo,
  },
  {
    id: 8,
    name: "Bajaj Finserv",
    logo: BajajLogo,
  },
  {
    id: 9,
    name: "IDFC FIRST Bank",
    logo: IDFCLogo,
  },
  {
    id: 10,
    name: "Yes Bank",
    logo: YesBankLogo,
  },
];

function TrustedPartners() {
  const scrollRef = useRef(null);
  const isInteractingRef = useRef(false);
  const resumeTimeoutRef = useRef(null);

  // Quadruple list for continuous infinite looping in both forward and backward directions
  const repeatedPartners = [
    ...partners,
    ...partners,
    ...partners,
    ...partners,
  ];

  // Pause auto-scrolling temporarily after user interaction
  const pauseAutoScroll = useCallback((duration = 2500) => {
    isInteractingRef.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      isInteractingRef.current = false;
    }, duration);
  }, []);

  const handlePrev = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    pauseAutoScroll(3000);

    const singleSetWidth = container.scrollWidth / 4;
    // If close to left boundary, seamlessly offset forward by 1 full set before scrolling back
    if (singleSetWidth > 0 && container.scrollLeft < 300) {
      container.scrollLeft += singleSetWidth;
    }

    container.scrollBy({ left: -260, behavior: "smooth" });
  }, [pauseAutoScroll]);

  const handleNext = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    pauseAutoScroll(3000);

    const singleSetWidth = container.scrollWidth / 4;
    // If close to right boundary, seamlessly offset backward by 1 full set
    if (singleSetWidth > 0 && container.scrollLeft >= singleSetWidth * 2.5) {
      container.scrollLeft -= singleSetWidth;
    }

    container.scrollBy({ left: 260, behavior: "smooth" });
  }, [pauseAutoScroll]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const getSingleSetWidth = () => container.scrollWidth / 4;

    // Initialize scroll position in the middle so Prev button works immediately on initial load
    const initScroll = () => {
      const singleSetWidth = getSingleSetWidth();
      if (singleSetWidth > 0 && container.scrollLeft === 0) {
        container.scrollLeft = singleSetWidth;
      }
    };

    const timer = setTimeout(initScroll, 60);

    let animationId;
    let lastTimestamp = performance.now();

    const animate = (currentTimestamp) => {
      const delta = currentTimestamp - lastTimestamp;
      lastTimestamp = currentTimestamp;

      if (!isInteractingRef.current && container) {
        // Continuous smooth auto-scrolling (~42px/sec)
        const step = (42 * delta) / 1000;
        container.scrollLeft += step;

        const singleSetWidth = getSingleSetWidth();
        if (singleSetWidth > 0) {
          if (container.scrollLeft >= singleSetWidth * 2.5) {
            container.scrollLeft -= singleSetWidth;
          } else if (container.scrollLeft <= 50) {
            container.scrollLeft += singleSetWidth;
          }
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(animationId);
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    isInteractingRef.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  };

  const handleMouseLeave = () => {
    pauseAutoScroll(1500);
  };

  return (
    <section className="overflow-hidden bg-white py-12 sm:py-14 lg:py-16">
      <div className="section-container">
        {/* ================= SECTION HEADER ================= */}
        <div className="text-center max-w-[700px] mx-auto">
          <p className="font-jakarta text-[11px] font-medium tracking-[0.34em] text-[var(--hero-green)] sm:text-[12px] uppercase mb-2.5">
            LENDING NETWORK
          </p>
          <h2 className="font-bricolage text-[28px] sm:text-[34px] md:text-[38px] font-bold leading-[1.15] tracking-[-1px] text-[#111827]">
            Trusted By{" "}
            <span className="text-[var(--hero-green)]">25+ Leading</span> Partners
          </h2>
          <p className="mt-2.5 font-jakarta text-[14px] sm:text-[15px] leading-[1.6] text-[#6b7280]">
            We partner with India's top banks and NBFCs to bring you the best loan offers.
          </p>
        </div>

        {/* ================= CONTINUOUS LOGO MARQUEE SLIDER ================= */}
        <div className="relative mt-8 sm:mt-10">
          {/* Left Navigation Arrow */}
          <button
            type="button"
            onClick={handlePrev}
            aria-label="Previous Partners"
            className="absolute -left-2 sm:-left-4 top-1/2 -translate-y-1/2 z-20 flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-[#374151] shadow-md transition-all hover:bg-[var(--hero-green)] hover:text-white hover:border-[var(--hero-green)] hover:scale-105 active:scale-95 cursor-pointer outline-none focus:outline-none"
          >
            <ChevronLeft size={18} strokeWidth={2.5} />
          </button>

          {/* Right Navigation Arrow */}
          <button
            type="button"
            onClick={handleNext}
            aria-label="Next Partners"
            className="absolute -right-2 sm:-right-4 top-1/2 -translate-y-1/2 z-20 flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-[#374151] shadow-md transition-all hover:bg-[var(--hero-green)] hover:text-white hover:border-[var(--hero-green)] hover:scale-105 active:scale-95 cursor-pointer outline-none focus:outline-none"
          >
            <ChevronRight size={18} strokeWidth={2.5} />
          </button>

          {/* Gradient Masks on Left & Right Edges for Smooth Fade */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 sm:w-20 bg-gradient-to-r from-white to-transparent z-10"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 sm:w-20 bg-gradient-to-l from-white to-transparent z-10"
          />

          {/* Marquee Track Container */}
          <div
            ref={scrollRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleMouseEnter}
            onTouchEnd={handleMouseLeave}
            className="flex gap-4 sm:gap-5 overflow-x-auto py-3 select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {repeatedPartners.map((item, idx) => (
              <div
                key={`${item.id}-${idx}`}
                className="flex h-[72px] sm:h-[80px] min-w-[175px] sm:min-w-[195px] max-w-[215px] items-center justify-center rounded-[16px] border border-[#e5e7eb] bg-white px-5 py-2.5 shadow-[0_2px_8px_rgba(0,0,0,0.03)] transition-all duration-300 hover:border-[var(--hero-green)]/60 hover:shadow-[0_8px_20px_rgba(0,135,81,0.12)] hover:-translate-y-0.5 select-none shrink-0"
              >
                <img
                  src={item.logo}
                  alt={item.name}
                  loading="lazy"
                  className="max-h-[38px] sm:max-h-[44px] max-w-[130px] sm:max-w-[150px] w-auto h-auto object-contain transition-transform duration-200 hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default TrustedPartners;


